import { DAILY_PRESETS, DAILY_UI } from "@/data/dailyReminders";
import { scheduleReminders } from "@/lib/push";
import { getLearn, putLearn } from "@/lib/learnSync";
import { LANG_TAG } from "@/lib/geo";
import { fill } from "@/lib/utils";

// Daily Umrah reminders (device-local time): settings {presetId: {on, time, days?}, custom: [{id,title,time,on,days?}], t}
// in localStorage + account (learn.daily_reminders), scheduled server-side as push kind "daily" for the next 14 days.
// `days` = JS getDay() numbers the reminder fires on; missing or all 7 → every day.
export const DAILY_KEY = "daily_reminders";
const DAYS_AHEAD = 14;

export const WEEK = [1, 2, 3, 4, 5, 6, 0]; // Monday → Sunday
export const isEveryDay = (days) => !Array.isArray(days) || days.length >= 7;
export const activeOnDay = (days, date) => isEveryDay(days) || days.includes(date.getDay());
// Localized weekday names keyed by JS day number (7 Jan 2024 was a Sunday).
export const weekdayNames = (lang, style = "short") =>
  Object.fromEntries(WEEK.map((d) => [d, new Date(2024, 0, 7 + d).toLocaleDateString(LANG_TAG[lang] || "en-US", { weekday: style })]));
export const daysLabel = (days, lang, tc) => {
  if (isEveryDay(days)) return tc(DAILY_UI.daily);
  const names = weekdayNames(lang);
  return WEEK.filter((d) => days.includes(d)).map((d) => names[d]).join(", ");
};

export const defaultSettings = () => Object.fromEntries(DAILY_PRESETS.map((p) => [p.id, { on: false, time: p.time }]));
export const readDaily = () => {
  try { return { ...defaultSettings(), custom: [], ...(JSON.parse(localStorage.getItem(DAILY_KEY)) || {}) }; } catch { return { ...defaultSettings(), custom: [] }; }
};
export const writeDaily = (s) => localStorage.setItem(DAILY_KEY, JSON.stringify(s));
export const enabledPresets = (s) => DAILY_PRESETS.filter((p) => s[p.id]?.on);
export const enabledCustom = (s) => (s.custom || []).filter((c) => c.on);
// Custom reminders are keyed "c_<id>" in the worship log.
export const customKey = (c) => `c_${c.id}`;

export function buildDailyReminders(settings, tc, now = new Date()) {
  const items = [];
  const push = (time, title, body, url, days) => {
    const [h, m] = String(time).split(":").map(Number);
    for (let d = 0; d < DAYS_AHEAD; d++) {
      const at = new Date(now);
      at.setDate(at.getDate() + d);
      at.setHours(h, m, 0, 0);
      if (at <= now || !activeOnDay(days, at)) continue;
      items.push({ send_at: at.toISOString(), title, body, url });
    }
  };
  for (const p of DAILY_PRESETS) {
    const cfg = settings[p.id];
    if (cfg?.on) push(cfg.time || p.time, tc(p.title), tc(p.body), p.url, cfg.days);
  }
  for (const c of enabledCustom(settings)) push(c.time, c.title, fill(tc(DAILY_UI.customBody), { title: c.title }), c.medkit ? "/health" : "/pagi-petang", c.days);
  return items.sort((a, b) => a.send_at.localeCompare(b.send_at));
}

// All server writes go through one promise chain so an older POST can never overwrite a newer one;
// each task re-reads the latest settings at execution time.
let chain = Promise.resolve();
const serialize = (fn) => { const p = chain.then(fn, fn); chain = p.catch(() => {}); return p; };

// Persist locally now; then (serialized) mirror to the account and replace the server-side "daily" queue.
export function syncDaily(settings, tc) {
  writeDaily(settings);
  return serialize(async () => {
    const s = readDaily();
    putLearn({ daily_reminders: s });
    await scheduleReminders(buildDailyReminders(s, tc), "daily").catch(() => null);
    return s;
  });
}

// On app start: adopt newer account settings (unless the device changed them meanwhile), then roll
// the 14-day window forward.
export function renewDaily(tc) {
  return serialize(async () => {
    const remote = (await getLearn())?.daily_reminders;
    const local = readDaily();
    let s = local;
    if (remote && (remote.t || 0) > (local.t || 0)) { s = { ...defaultSettings(), custom: [], ...remote }; writeDaily(s); }
    if (enabledPresets(s).length || enabledCustom(s).length) await scheduleReminders(buildDailyReminders(s, tc), "daily").catch(() => null);
    return s;
  });
}
